// Shared vehicle-assignment modal — opened from a Tour Timeline row or from
// Tour detail. Replaces the tour's full vehicle set via PUT /tours/:id/vehicles.

import React, { useEffect, useState } from "react";
import { useApi } from "../apiHelpers";
import { useToast } from "../components/Toast/ToastContext";
import { buttonClass } from "../components/ui/Button";
import Checkbox from "../components/ui/Checkbox";
import { Tag } from "../components/ui/Tag";
import Dialog from "../components/ui/Dialog";
import { Skeleton, SkeletonGroup } from "../components/ui/Skeleton";
import EmptyState from "../components/ui/EmptyState";
import FadeIn from "../components/ui/FadeIn";
import type { Vehicle } from "../Vehicles/VehiclesPage";

export interface TourVehicle {
	id: string;
	display_code: string;
	operational_status: string;
	name: string | null;
	vehicle_class: string | null;
	drone_capacity: number | null;
	crew_capacity: number | null;
	can_tow: boolean | null;
}

interface Props {
	tourId: string;
	tourName: string;
	assigned: TourVehicle[];
	onClose: () => void;
	/** Called with the updated tour payload after any successful save. */
	onSaved: (updatedTour: any) => void;
}

const VehicleAssignmentModal: React.FC<Props> = ({
	tourId,
	tourName,
	assigned,
	onClose,
	onSaved,
}) => {
	const api = useApi();
	const toast = useToast();

	const [vehicles, setVehicles] = useState<Vehicle[]>([]);
	const [loading, setLoading] = useState(true);
	const [selected, setSelected] = useState<Set<string>>(
		new Set(assigned.map((v) => v.id)),
	);
	const [saving, setSaving] = useState(false);
	// What the dialog opened with; the checklist is dirty only when it differs.
	const [opened] = useState(() => new Set(assigned.map((v) => v.id)));
	const dirty =
		selected.size !== opened.size || Array.from(selected).some((id) => !opened.has(id));

	useEffect(() => {
		let cancelled = false;
		const run = async () => {
			try {
				const rows: Vehicle[] = await api.get("/vehicles");
				if (!cancelled) setVehicles(rows);
			} catch {
				if (!cancelled) toast.error("Failed to load vehicles");
			} finally {
				if (!cancelled) setLoading(false);
			}
		};
		run();
		return () => {
			cancelled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const toggle = (id: string) => {
		setSelected((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	};

	const handleSave = async () => {
		setSaving(true);
		let updated;
		try {
			updated = await api.put(`/tours/${tourId}/vehicles`, {
				body: { asset_ids: Array.from(selected) },
			});
		} catch {
			toast.error("Failed to update vehicles");
			setSaving(false);
			return;
		}
		toast.success("Vehicles updated");
		onSaved(updated);
		setSaving(false);
	};

	// Selectable = active fleet; an OOC rig can still be picked (it may be back
	// by tour time) but shows its status so the choice is informed. Retired
	// vehicles only appear if they're already assigned (so they can be removed).
	const selectable = vehicles.filter(
		(v) => v.operational_status !== "retired" || selected.has(v.id),
	);

	const droneSum = selectable
		.filter(
			(v) =>
				selected.has(v.id) &&
				!["ooc", "retired"].includes(v.operational_status),
		)
		.reduce((s, v) => s + (v._extension?.drone_capacity ?? 0), 0);
	const crewSum = selectable
		.filter(
			(v) =>
				selected.has(v.id) &&
				!["ooc", "retired"].includes(v.operational_status),
		)
		.reduce((s, v) => s + (v._extension?.crew_capacity ?? 0), 0);

	return (
		<Dialog
			open
			onClose={onClose}
			title="Vehicles"
			meta={tourName}
			size="md"
			busy={saving}
			dirty={dirty}
			footer={
				<>
					<button
						type="button"
						onClick={onClose}
						className={buttonClass("quiet")}
					>
						Cancel
					</button>
					<button
						onClick={handleSave}
						disabled={saving || loading}
						className={buttonClass("filled")}
					>
						{saving ? "Saving…" : "Save"}
					</button>
				</>
			}
		>
			<div className="space-y-4">
				{loading && (
					<SkeletonGroup label="Loading vehicles" className="space-y-1.5">
						<Skeleton className="h-12 w-full" />
						<Skeleton className="h-12 w-full" />
					</SkeletonGroup>
				)}

				{!loading && selectable.length === 0 && (
					<FadeIn>
						<EmptyState variant="area">
							No vehicles in the fleet yet — add them on the Vehicles
							page first.
						</EmptyState>
					</FadeIn>
				)}

				{!loading && selectable.length > 0 && (
					<FadeIn className="space-y-1.5">
						{selectable.map((v) => {
							const ext = v._extension;
							const unusable = ["ooc", "retired"].includes(
								v.operational_status,
							);
							const otherTours = v.assigned_tours.filter(
								(t) => t.id !== tourId,
							);
							return (
								<Checkbox
									key={v.id}
									fill
									checked={selected.has(v.id)}
									onChange={() => toggle(v.id)}
									className={`px-3 py-2 rounded-md border transition-colors ${
										selected.has(v.id)
											? "border-primary/50 bg-primary/5"
											: "border-gray-700/60 hover:bg-gray-700/30"
									}`}
									label={
										<span className="block truncate">
											{ext?.name ?? v.display_code}
											{unusable && (
												<Tag size="sm" tone="bad" className="ml-2">
													{v.operational_status}
												</Tag>
											)}
										</span>
									}
									description={
										<>
											{ext?.drone_capacity ?? 0} drones ·{" "}
											{ext?.crew_capacity ?? 0} seats
											{ext?.can_tow ? " · tows" : ""}
											{otherTours.length > 0 && (
												<span className="text-amber-400/80">
													{" · also on "}
													{otherTours
														.map((t) => t.name)
														.join(", ")}
												</span>
											)}
										</>
									}
								/>
							);
						})}
					</FadeIn>
				)}

				{!loading && (
					<FadeIn className="text-xs text-on-surface-variant border-t border-gray-700/40 pt-3">
						Selected capacity:{" "}
						<span className="text-primary font-semibold">
							{droneSum}
						</span>{" "}
						drones ·{" "}
						<span className="text-primary font-semibold">
							{crewSum}
						</span>{" "}
						seats
					</FadeIn>
				)}
			</div>
		</Dialog>
	);
};

export default VehicleAssignmentModal;
